/**
 * @class CrossSellRecommendations
 * @extends HTMLElement
 * @description Bridges `cart:add:success` to `cart:added` and optionally opens the recommendations dialog when the originating product-form has cross-sell enabled (product metafield code_cross_sell.enabled).
 */
class CrossSellRecommendations extends HTMLElement {
  static DIALOG_ID = 'add-to-cart-cross-sell-dialog';

  /** @type {(() => void) | undefined} */
  #unsubscribeCartAdd;

  /** @type {(() => void) | undefined} */
  #unsubscribeDialogClose;

  /** @type {(() => void) | undefined} */
  #unsubscribeCartAdded;

  /**
   * @method connectedCallback
   * @description Subscribes to `cart:add:success` when the element is connected to the DOM.
   * @returns {void}
   */
  connectedCallback() {
    this.#unsubscribeCartAdd = subscribe('cart:add:success', (payload) => {
      void this.#handleCartAddSuccess(payload);
    });
  }

  /**
   * @method disconnectedCallback
   * @description Cleans up all active PubSub subscriptions when the element is removed from the DOM.
   * @returns {void}
   */
  disconnectedCallback() {
    this.#unsubscribeCartAdd?.();
    this.#unsubscribeCartAdd = undefined;
    this.#clearDialogCloseSubscription();
    this.#clearCartAddedSubscription();
  }

  /**
   * @method #clearDialogCloseSubscription
   * @description Unsubscribes from the `dialog:closed` event if active.
   * @returns {void}
   */
  #clearDialogCloseSubscription() {
    this.#unsubscribeDialogClose?.();
    this.#unsubscribeDialogClose = undefined;
  }

  /**
   * @method #setupDialogCloseSubscription
   * @description Subscribes to `dialog:closed:<DIALOG_ID>`. When fired, cleans up subscriptions and opens the cart drawer.
   * @returns {void}
   */
  #setupDialogCloseSubscription() {
    this.#clearDialogCloseSubscription();
    this.#unsubscribeDialogClose = subscribe(
      `dialog:closed:${CrossSellRecommendations.DIALOG_ID}`,
      () => {
        this.#clearDialogCloseSubscription();
        this.#clearCartAddedSubscription();
        document.getElementById('cart-drawer')?.open?.();
      }
    );
  }

  /**
   * @method #clearCartAddedSubscription
   * @description Unsubscribes from the `cart:added` event if active.
   * @returns {void}
   */
  #clearCartAddedSubscription() {
    this.#unsubscribeCartAdded?.();
    this.#unsubscribeCartAdded = undefined;
  }

  /**
   * @method #setupCartAddedSubscription
   * @description Subscribes to `cart:added` while the dialog is open. When a product is added to cart
   * from within the recommendations dialog, this closes the dialog (which in turn opens the cart drawer).
   * @returns {void}
   */
  #setupCartAddedSubscription() {
    this.#clearCartAddedSubscription();
    this.#unsubscribeCartAdded = subscribe('cart:added', () => {
      this.#clearCartAddedSubscription();
      publish('dialog:close', { dialogId: CrossSellRecommendations.DIALOG_ID });
    });
  }

  /**
   * @method #fetchRecommendationsHtml
   * @description Fetches the recommendations section HTML via the Shopify Section Rendering API and
   * returns the outer HTML of the `[data-product-recommendations]` element if slides are found, or `null` otherwise.
   * @param {number|string} productId - The product ID to fetch recommendations for.
   * @returns {Promise<string|null>}
   */
  async #fetchRecommendationsHtml(productId) {
    const { sectionId, intent, limit } = this.dataset;
    if (
      !sectionId ||
      !intent ||
      limit === undefined ||
      limit === '' ||
      !routes.product_recommendations_url
    )
      return null;

    const params = new URLSearchParams({
      section_id: sectionId,
      product_id: String(productId),
      intent,
      limit: String(limit),
    });
    const url = `${routes.product_recommendations_url}?${params.toString()}`;
    const response = await fetch(url);
    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const recRoot = doc.querySelector('[data-product-recommendations]');
    const slideCount = recRoot?.querySelectorAll('.swiper-slide').length ?? 0;
    if (slideCount > 0 && recRoot) return recRoot.outerHTML;
    return null;
  }

  /**
   * @method #handleCartAddSuccess
   * @description Handles the `cart:add:success` event. Fetches recommendations for the first added product,
   * injects them into the dialog mount, and opens the dialog. Falls back to publishing `cart:added`
   * (opening the cart drawer normally) if no recommendations are found or an error occurs.
   * @param {{ items?: Array<{ product_id?: number }>; quantity?: number|string }} payload
   * @returns {Promise<void>}
   */
  async #handleCartAddSuccess({ items, quantity }) {
    const firstProductId = items?.[0]?.product_id;
    const mount = document.querySelector('[data-cross-sell-recommendations-mount]');

    if (firstProductId && mount && routes.product_recommendations_url && this.dataset.sectionId) {
      try {
        const recHtml = await this.#fetchRecommendationsHtml(firstProductId);
        if (recHtml) {
          mount.innerHTML = recHtml;
          publish('cart:added', {
            items,
            quantity,
            skipDrawerOpen: true,
          });
          this.#setupDialogCloseSubscription();
          this.#setupCartAddedSubscription();
          publish('dialog:open', { dialogId: CrossSellRecommendations.DIALOG_ID });
          return;
        }
      } catch (error) {
        console.error('Cross-sell recommendations fetch failed:', error);
      }
    }

    publish('cart:added', {
      items,
      quantity,
    });
  }
}

customElements.define('cross-sell-recommendations', CrossSellRecommendations);
